import * as React from 'react';
import { action } from 'mobx';

import { Permissions, roleMapping } from '@app/AppConstants';

import { appStore } from '@app/AppStore';

import { Icon } from '@app/Components/Icon';
import { modalService } from './Modal';
import ImpersonationDialog from '@app/Components/Dialogs/ImpersonationDialog';
import { securityService } from '@app/Services/SecurityService';

export interface IWindowAdal extends Window {
    adal?: {
        redirectToLogin(): void;
        redirectToLogout(): void;
    };
}

export class LoginSection extends React.Component<{}, {}> {
    componentDidMount() {
        window.addEventListener('storage', this.logOutEventHandler);
    }

    componentWillUnmount() {
        window.removeEventListener('storage', this.logOutEventHandler);
    }

    logOutEventHandler = (event: StorageEvent) => {
        if (event.key === 'logout-event') {
            AzureAuth.logOut();
        }
    };

    render() {
        const currentUser = appStore.currentUser;
        const userName = currentUser ? currentUser.name || currentUser.unique_name : 'guest';
        return (
            <div className="user">
                <span className="user-name">Hello, {userName}</span>
                {this._renderImpersonation()}
                <i className="ms-Icon ms-Icon--SignOut" aria-hidden="true" title="Log out" onClick={() => AzureAuth.logOut(true)} />
            </div>
        );
    }

    private _renderImpersonation() {
        if (!securityService.hasPermission(Permissions.CanImpersonate, AzureAuth.isRealUserInAdminGroup) && !securityService.hasOriginalPermission(Permissions.CanImpersonate)) return null;
        if (appStore.impersonatedUser) {
            return (
                <>
                    <Icon name="user-secret" title={'Roles: ' + appStore.currentUserRoles.map(role => {
                        role = role.replace('PersonData.', '');
                        return roleMapping[role] ?? role;
                    }).join(', ')} onClick={this._onImpersonateClick} />
                    <Icon name="eye-slash" title="Exit impersonation" onClick={AzureAuth.exitImpersonation} />
                </>
            );
        }
        return <Icon name="eye" title="Impersonation" onClick={this._onImpersonateClick} />;
    }

    @action.bound
    async _onImpersonateClick() {
        await modalService.show(ImpersonationDialog, {
            canImpersonateAsAdmin: securityService.hasPermission(Permissions.CanImpersonateAsAdmin, AzureAuth.isRealUserInAdminGroup) || securityService.hasOriginalPermission(Permissions.CanImpersonateAsAdmin)
        });
    }
}

export class AzureAuth {

    static logOut = (all?: boolean) => {
        appStore.clearUserData();
        const appWindow: IWindowAdal = window;
        all && appStore.setValue('logout-event', 'logout' + Math.random());
        appWindow.adal && appWindow.adal.redirectToLogout();
    };

    static logOutImpersonation = () => {
        localStorage.removeItem('impersonatedUser');
        window.location.href = '/';
    };
    
    static login = () => {
        appStore.clearUserData();
        const appWindow: IWindowAdal = window;
        appWindow.adal && appWindow.adal.redirectToLogin();
    };

    static exitImpersonation = () => {
        AzureAuth.logOutImpersonation();
    };

    static get isUserInAdminGroup(): boolean {
        const roles = appStore.currentUserRoles;
        return roles.indexOf(UserRoles.administrator) > -1;
    }

    static get isRealUserInAdminGroup(): boolean {
        const roles = appStore.realUserRoles;
        return roles.indexOf(UserRoles.administrator) > -1;
    }

    static get isUserInHumanResourcesGroup() {
        const roles = appStore.currentUserRoles;
        return roles.indexOf(UserRoles.humanResources) > -1;
    }

    static get isUserOnlyInCrewGroup() {
        const roles = appStore.currentUserRoles;
        return !this.isUserHasReadRole && roles.indexOf(UserRoles.crew) > -1;
    }

    static get isUserHasReadRole() {
        const roles = appStore.currentUserRoles;
        return roles && (roles.indexOf(UserRoles.administrator) > -1
            || roles.indexOf(UserRoles.reader) > -1
            || roles.indexOf(UserRoles.support) > -1
            || roles.indexOf(UserRoles.cabinTraining) > -1
            || roles.indexOf(UserRoles.cockpitTraining) > -1
            || roles.indexOf(UserRoles.marketing) > -1);
    }

    static get isUserHasAnyRole() {
        const roles = appStore.currentUserRoles;
        return roles.length !== 0;
    }
    
    static get isReadOnly(): boolean {
        const roles = appStore.currentUserRoles;
        if (roles.indexOf(UserRoles.administrator) > -1 || roles.indexOf(UserRoles.humanResources) > -1) {
            return false;
        }
        return this.isUserHasReadRole || this.isUserOnlyInCrewGroup;
    }
}

export class UserRoles {
    public static readonly administrator: string = 'PersonData.Administrator';
    public static readonly it: string = 'PersonData.IT';
    public static readonly crew: string = 'PersonData.Crew';
    public static readonly humanResources: string = 'PersonData.HumanResources';
    public static readonly reader: string = 'PersonData.Reader';
    public static readonly support: string =  'PersonData.Support';
    public static readonly cabinPlanning: string =  'PersonData.CabinPlanning';
    public static readonly cockpitPlanning: string =  'PersonData.CockpitPlanning';
    public static readonly crewOffice: string =  'PersonData.CrewOffice';
    public static readonly cockpitLine: string =  'PersonData.CockpitLine';
    public static readonly cabinLine: string =  'PersonData.CabinLine';
    public static readonly cabinTraining: string =  'PersonData.CabinTraining';
    public static readonly cockpitTraining: string =  'PersonData.CockpitTraining';
    public static readonly marketing: string =  'PersonData.Marketing';
}